import { SxProps, Theme } from '@mui/material'
import { AISLE_HEADER_BG, SLATE_200, SURFACE_MUTED, WHITE } from '../config/constants'

/** Lado del recuadro de cada slot en la vista visual. */
const SLOT_TILE = 108

const TABLE_HEADER_CELL = {
  bgcolor: SURFACE_MUTED,
  fontWeight: 'bold',
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
}

export const productsModalStyles = {
  header: {
    bgcolor: AISLE_HEADER_BG,
    color: WHITE,
    py: 1.5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as SxProps<Theme>,

  headerTitle: { lineHeight: 1.3, fontSize: '1rem' } as SxProps<Theme>,

  closeButton: { color: WHITE, mt: -0.5 } as SxProps<Theme>,

  content: { pt: 2, pb: 2 } as SxProps<Theme>,

  loadingWrapper: { display: 'flex', justifyContent: 'center', py: 4 } as SxProps<Theme>,

  errorText: { py: 2 } as SxProps<Theme>,

  // ── Métricas de la posición ───────────────────────────────────────────────

  statsBar: {
    display: 'flex',
    gap: 4,
    mb: 2,
    mt: 0.5,
    pb: 2,
    flexWrap: 'wrap',
    borderBottom: `1px solid ${SLATE_200}`,
  } as SxProps<Theme>,

  statLabel: {
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.5,
  } as SxProps<Theme>,

  statUnit: { ml: 0.5, fontWeight: 500, color: 'text.secondary' } as SxProps<Theme>,

  zoneChipList: { display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 } as SxProps<Theme>,

  zoneChip: { fontWeight: 600, height: 22, fontSize: '0.72rem' } as SxProps<Theme>,

  /** Subrayado punteado: señala que el tooltip trae la lista completa de zonas. */
  zoneCount: { cursor: 'default', textDecoration: 'underline dotted' } as SxProps<Theme>,

  // ── Selector Visual / Tabla ───────────────────────────────────────────────

  viewToggle: { display: 'flex', gap: 1, mb: 2 } as SxProps<Theme>,

  viewToggleButton: { textTransform: 'none', minWidth: 80 } as SxProps<Theme>,

  // ── Vista visual: un recuadro por slot ────────────────────────────────────

  slotCountLabel: {
    fontFamily: 'monospace',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: 'text.secondary',
    mb: 1.5,
  } as SxProps<Theme>,

  slotGrid: { display: 'flex', flexWrap: 'wrap', gap: 1.5 } as SxProps<Theme>,

  slotTile: (background: string): SxProps<Theme> => ({
    width: SLOT_TILE,
    minHeight: SLOT_TILE,
    bgcolor: background,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    cursor: 'pointer',
    px: 1,
    transition: 'filter 0.12s, transform 0.1s',
    '&:hover': { filter: 'brightness(1.12)', transform: 'translateY(-1px)' },
    '&:active': { transform: 'translateY(0)' },
  }),

  /** Velo oscuro detrás del nombre: el slot se lee sobre cualquier color de marca. */
  slotNameBadge: {
    bgcolor: 'rgba(0,0,0,0.22)',
    borderRadius: 1,
    px: 1,
    py: 0.25,
  } as SxProps<Theme>,

  slotName: {
    fontSize: '0.72rem',
    fontWeight: 800,
    color: WHITE,
    lineHeight: 1,
    fontFamily: 'monospace',
  } as SxProps<Theme>,

  slotQty: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: WHITE,
    lineHeight: 1,
    textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  } as SxProps<Theme>,

  slotBrand: {
    fontSize: '0.68rem',
    color: 'rgba(255,255,255,0.88)',
    lineHeight: 1.2,
    textAlign: 'center',
    px: 0.5,
  } as SxProps<Theme>,

  // ── Vista de tabla ────────────────────────────────────────────────────────

  filterRow: { display: 'flex', gap: 1.5, mb: 1.5 } as SxProps<Theme>,

  filterInput: { flex: 1, borderRadius: 1 } as SxProps<Theme>,

  filterIcon: { fontSize: 18, color: 'text.disabled', mr: 0.5 } as SxProps<Theme>,

  slotFilterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1,
  } as SxProps<Theme>,

  slotFilterChip: {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '0.78rem',
  } as SxProps<Theme>,

  emptyResults: { py: 2, textAlign: 'center' } as SxProps<Theme>,

  tableContainer: { maxHeight: 340, borderRadius: 1 } as SxProps<Theme>,

  headerCell: TABLE_HEADER_CELL as SxProps<Theme>,

  headerCellSlot: { ...TABLE_HEADER_CELL, width: 90 } as SxProps<Theme>,

  headerCellBatch: { ...TABLE_HEADER_CELL, width: 120 } as SxProps<Theme>,

  /** Celda del toggle SKU/EAN: sólo el fondo, el resto lo aportan sus hijos. */
  headerCellToggle: { bgcolor: SURFACE_MUTED } as SxProps<Theme>,

  toggleRow: { display: 'flex', alignItems: 'center', gap: 0 } as SxProps<Theme>,

  toggleLabel: { fontSize: '0.7rem', textTransform: 'uppercase' } as SxProps<Theme>,

  slotCell: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: 'text.secondary',
  } as SxProps<Theme>,

  skuCell: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    color: 'text.primary',
  } as SxProps<Theme>,

  brandCell: { display: 'flex', alignItems: 'center', gap: 1 } as SxProps<Theme>,

  brandDot: (color: string): SxProps<Theme> => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    bgcolor: color,
    flexShrink: 0,
  }),

  /** El color viene del estado de vencimiento del lote; sin lote queda neutro. */
  batchChip: (expiry: { bgcolor: string; color: string } | null): SxProps<Theme> => ({
    height: 20,
    fontSize: '0.68rem',
    cursor: 'default',
    fontWeight: 600,
    fontFamily: 'monospace',
    ...(expiry && { bgcolor: expiry.bgcolor, color: expiry.color }),
  }),
}
